import type { ReactElement } from "react";
import type { CalendarDate } from "@internationalized/date";
import { useRef, useState } from "react";

import type { CalendarHandle } from "../calendar/use-calendar-handle";
import type { InputTextProperties } from "../input-text";

import { DateInput } from "./date-input";
import { Dropdown, useDropdown } from "./dropdown";
import { MobileView } from "./mobile-view";
import { useCalendarScroll } from "./use-calendar-scroll";
import { useMatchMedia } from "../../hooks/use-match-media";
import { BREAKPOINT_MD } from "@jetblue/kit-tailwind-config/theme";
import type { RenderDate } from "../calendar/day";
import type { Tuple } from "../../types";
import { useBlockUnderlayScrollWhileOpen } from "./use-block-underlay-scroll-while-open";
import { AnnounceSelectedDate } from "./announce-selected-date";

export type SingleDatePickerLabels = {
  close: string;
  done: string;
  input: string;
  reset: string;
};

type FocusState = "start" | "none";

export type SingleDatePickerProperties = {
  className?: string | undefined;
  endDate: CalendarDate;
  error?: InputTextProperties["error"] | undefined;
  footerSlot?: ReactElement | undefined;
  headingSlot?: ReactElement | undefined;
  isRequired?: boolean | undefined;
  labels: SingleDatePickerLabels;
  renderDate?: RenderDate;
  selectedDate: CalendarDate | undefined;
  setSelectedDate: (date: CalendarDate | undefined) => void;
  showValidation?: boolean | undefined;
  startDate: CalendarDate;
};
export const SingleDatePicker = ({
  className,
  endDate,
  error,
  footerSlot,
  headingSlot,
  isRequired,
  labels,
  renderDate,
  selectedDate,
  setSelectedDate,
  showValidation = false,
  startDate,
}: SingleDatePickerProperties) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusState, setFocusState] = useState<FocusState>("none");
  const inputReference = useRef<HTMLInputElement | null>(null);
  const { target, floating, refs } = useDropdown({
    isOpen,
    onOpenChange: setIsOpen,
  });
  const desktopCalendarReference = useRef<CalendarHandle | null>(null);
  const mobileCalendarReference = useRef<CalendarHandle | null>(null);

  // IMPORTANT: The media query below must be kept in sync with the one used in the MobileView component.
  // Both use the same breakpoint (md) to determine mobile vs desktop behavior.
  // If these queries ever differ by even 1px, the DateInput may become readonly at a different width than MobileView switches to mobile layout.
  // This causes a confusing user experience: the input could be readonly while the calendar is still in desktop mode, or vice versa.
  // Always update both places together to ensure consistent breakpoints and avoid off-by-one errors.
  const isMobile = !useMatchMedia(`(min-width: ${BREAKPOINT_MD})`);
  const activeCalendarReference = isMobile
    ? mobileCalendarReference
    : desktopCalendarReference;

  const selectedDates: Tuple<CalendarDate | undefined> = [
    selectedDate,
    undefined,
  ];

  useCalendarScroll(
    isOpen,
    isMobile,
    focusState,
    selectedDates,
    activeCalendarReference,
  );

  useBlockUnderlayScrollWhileOpen({ isMobile, isOpen });

  return (
    <div {...target} className={className} ref={refs.setReference}>
      <AnnounceSelectedDate selectedDate={selectedDate} />
      <DateInput
        className="peer"
        closeCalendar={() => {
          setIsOpen(false);
        }}
        error={error}
        isCalendarOpen={isOpen}
        isRequired={isRequired}
        label={labels.input}
        max={endDate}
        min={startDate}
        onChange={setSelectedDate}
        onFocus={() => {
          setIsOpen(true);
        }}
        onFocusChange={(focused) => {
          setFocusState(focused ? "start" : "none");
        }}
        readOnly={isMobile}
        ref={inputReference}
        showValidation={showValidation}
        value={selectedDate}
      />
      <Dropdown
        calendarRef={desktopCalendarReference}
        endDate={endDate}
        footerSlot={footerSlot}
        isOpen={isOpen}
        labels={labels}
        onDateClick={(date: CalendarDate) => {
          setSelectedDate(date);
          setIsOpen(false);
        }}
        onDone={() => {
          setIsOpen(false);
        }}
        rangeStart={selectedDate}
        ref={refs.setFloating}
        renderDate={renderDate}
        startDate={startDate}
        {...floating}
      />
      <MobileView
        calendarRef={mobileCalendarReference}
        dateInputConfigs={[
          {
            focused: true,
            label: labels.input,
            onFocus: () => {
              setIsOpen(true);
            },
            value: selectedDate,
          },
        ]}
        endDate={endDate}
        footerSlot={footerSlot}
        headingSlot={headingSlot}
        isOpen={isOpen}
        labels={labels}
        onCloseClick={() => {
          setIsOpen(false);
        }}
        onDateClick={setSelectedDate}
        onResetClick={() => {
          setSelectedDate(undefined);
        }}
        renderDate={renderDate}
        startDate={startDate}
      />
    </div>
  );
};
SingleDatePicker.displayName = "DatePicker.Single";
